import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { ArrowLeft, FileText, CheckCircle, Clock, Pen, Download, Eraser, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Signature Pad Component
const SignaturePad = ({ onSave, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = 160;
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Draw baseline
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#d1d5db';
    ctx.beginPath();
    ctx.moveTo(20, canvas.height - 30);
    ctx.lineTo(canvas.width - 20, canvas.height - 30);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#1a1a1a';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = () => {
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Redraw baseline
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#d1d5db';
    ctx.beginPath();
    ctx.moveTo(20, canvas.height - 30);
    ctx.lineTo(canvas.width - 20, canvas.height - 30);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    setHasDrawn(false);
    onClear && onClear();
  };

  const getSignatureData = useCallback(() => {
    if (!hasDrawn) return null;
    return canvasRef.current.toDataURL('image/png');
  }, [hasDrawn]);

  // Expose getSignatureData to parent
  useEffect(() => {
    onSave && onSave(getSignatureData);
  }, [getSignatureData, onSave]);

  return (
    <div className="space-y-2">
      <div className="border-2 border-dashed rounded-lg bg-white relative touch-none">
        <canvas
          ref={canvasRef}
          className="w-full cursor-crosshair"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
          data-testid="signature-canvas"
        />
        <span className="absolute bottom-2 left-4 text-xs text-muted-foreground pointer-events-none">Sign above the line</span>
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={clear} className="text-muted-foreground">
          <Eraser className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
};

const AgreementsPage = () => {
  const { user } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [parentGuardianName, setParentGuardianName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const getSignatureRef = useRef(null);

  useEffect(() => {
    fetchAgreements();
  }, []);

  useEffect(() => {
    if (user) setSignerName(user.name || '');
  }, [user]);

  const fetchAgreements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/agreements`);
      setAgreements(res.data.agreements || []);
    } catch {
      toast.error('Failed to load agreements');
    }
    setLoading(false);
  };

  const handleSign = async () => {
    if (!agreedToTerms) {
      toast.error('Please confirm you have read and agree to the terms');
      return;
    }
    if (!signerName.trim()) {
      toast.error('Please enter your full legal name');
      return;
    }
    const getSignature = getSignatureRef.current;
    const signatureData = getSignature ? getSignature() : null;
    if (!signatureData) {
      toast.error('Please draw your signature');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/agreements/${selectedAgreement.id}/sign`, {
        signature_data: signatureData,
        signer_name: signerName,
        parent_guardian_name: parentGuardianName || null,
      });
      toast.success('Agreement signed successfully!');
      setSelectedAgreement(null);
      setAgreedToTerms(false);
      setParentGuardianName('');
      fetchAgreements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign agreement');
    }
    setSubmitting(false);
  };

  const typeBadge = (type) => {
    const colors = {
      liability_waiver: 'bg-red-500/10 text-red-600 border-red-500/20',
      coaching_agreement: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      media_release: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
    };
    const labels = { liability_waiver: 'Liability Waiver', coaching_agreement: 'Coaching Agreement', media_release: 'Media Release', other: 'Other' };
    return <Badge className={colors[type] || 'bg-muted text-muted-foreground'}>{labels[type] || type}</Badge>;
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  const unsignedCount = agreements.filter(a => !a.signed).length;

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Link to="/" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight">My Agreements</h1>
          <p className="text-muted-foreground mt-2">Review and sign required agreements.</p>
        </div>

        {unsignedCount > 0 && (
          <Card className="mb-6 border-amber-500/30 bg-amber-500/5">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-700">
                You have <strong>{unsignedCount}</strong> unsigned agreement{unsignedCount !== 1 ? 's' : ''} that may be required for event registration.
              </p>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : agreements.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No agreements available at this time.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {agreements.map(ag => (
              <Card key={ag.id} className={ag.signed ? 'border-green-500/20' : 'border-amber-500/30'} data-testid={`agreement-card-${ag.id}`}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {typeBadge(ag.agreement_type)}
                        {ag.signed ? (
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                            <CheckCircle className="w-3 h-3 mr-1" /> Signed
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                            <Clock className="w-3 h-3 mr-1" /> Unsigned
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-display font-bold text-lg">{ag.title}</h3>
                      {ag.description && <p className="text-sm text-muted-foreground mt-1">{ag.description}</p>}
                      {ag.signed && ag.signed_at && (
                        <p className="text-xs text-muted-foreground mt-2">Signed on {formatDate(ag.signed_at)}</p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {ag.pdf_url && (
                        <Button variant="outline" size="sm" className="rounded-full" onClick={async () => {
                          try {
                            const res = await axios.get(`${API_URL}/api/agreements/${ag.id}/download`);
                            window.open(res.data.url, '_blank');
                          } catch { /* ignore */ }
                        }}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      )}
                      {!ag.signed ? (
                        <Button
                          onClick={() => { setSelectedAgreement(ag); setAgreedToTerms(false); }}
                          className="rounded-full"
                          data-testid={`sign-agreement-${ag.id}`}
                        >
                          <Pen className="w-4 h-4 mr-1" /> Sign
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" className="rounded-full" onClick={() => setSelectedAgreement(ag)}>
                          View
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Sign Agreement Dialog */}
        <Dialog open={!!selectedAgreement} onOpenChange={() => { setSelectedAgreement(null); setAgreedToTerms(false); }}>
          <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl uppercase">
                {selectedAgreement?.signed ? 'Agreement Details' : 'Sign Agreement'}
              </DialogTitle>
              <DialogDescription>{selectedAgreement?.title}</DialogDescription>
            </DialogHeader>

            {selectedAgreement && (
              <div className="space-y-6">
                {/* Agreement Content */}
                <div className="bg-muted/30 p-4 rounded-lg border max-h-64 overflow-y-auto">
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed">
                    {selectedAgreement.content}
                  </div>
                </div>

                {selectedAgreement.pdf_url && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API_URL}/api/agreements/${selectedAgreement.id}/download`);
                        window.open(res.data.url, '_blank');
                      } catch { /* ignore */ }
                    }}
                    className="text-primary hover:underline text-sm inline-flex items-center gap-1"
                  >
                    <Download className="w-4 h-4" /> Download full document
                  </button>
                )}

                {selectedAgreement.signed ? (
                  <div className="bg-green-500/5 border border-green-500/20 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-green-600 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">Signed</span>
                    </div>
                    <p className="text-sm text-muted-foreground">You signed this agreement on {formatDate(selectedAgreement.signed_at)}.</p>
                  </div>
                ) : (
                  <>
                    {/* Checkbox */}
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={e => setAgreedToTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-primary"
                        data-testid="agree-checkbox"
                      />
                      <span className="text-sm">
                        I have read and understand the terms of this agreement and agree to be bound by them.
                      </span>
                    </label>

                    {/* Signer Name */}
                    <div>
                      <Label>Full Legal Name</Label>
                      <Input
                        value={signerName}
                        onChange={e => setSignerName(e.target.value)}
                        placeholder="Your full legal name"
                        data-testid="signer-name-input"
                      />
                    </div>

                    {/* Parent/Guardian for youth */}
                    <div>
                      <Label>Parent/Guardian Name (if applicable)</Label>
                      <Input
                        value={parentGuardianName}
                        onChange={e => setParentGuardianName(e.target.value)}
                        placeholder="Parent or guardian full name"
                        data-testid="guardian-name-input"
                      />
                    </div>

                    {/* Signature Pad */}
                    <div>
                      <Label>Your Signature</Label>
                      <SignaturePad
                        onSave={(getSignature) => { getSignatureRef.current = getSignature; }}
                        onClear={() => {}}
                      />
                    </div>

                    {/* Submit */}
                    <div className="flex justify-end gap-3 pt-2">
                      <Button variant="outline" onClick={() => setSelectedAgreement(null)} className="rounded-full">Cancel</Button>
                      <Button
                        onClick={handleSign}
                        disabled={submitting || !agreedToTerms}
                        className="rounded-full"
                        data-testid="submit-signature-btn"
                      >
                        {submitting ? 'Signing...' : (
                          <><Pen className="w-4 h-4 mr-2" /> Sign Agreement</>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AgreementsPage;
